import * as React from 'react';
import { Result, ResultFactory, Campaign, CampaignFactory, Station, StationFactory, Parameter, ParameterFactory } from '../../resource/'; 
import { IAuthProps, List, IListState, IListProps, Query, SearchFilter } from '../../services/';

import { Form } from '@independent-software/typeui/controls/Form';
import { Dialog } from '@independent-software/typeui/controls/Dialog';
import { Panel } from '@independent-software/typeui/controls/Panel';
import { DataTable } from '@independent-software/typeui/controls/DataTable';

import { IconBar, Export } from '../../modules';
import { Checkbox } from '@independent-software/typeui/controls/Checkbox';
import { Gate } from '../../controls/Gate';
import { Dropdown } from '@independent-software/typeui/controls/Dropdown';
import { Number } from '@independent-software/typeui/formatters/Number';
import { Icon } from '@independent-software/typeui/controls/Icon';
import { Datum } from '@independent-software/typeui/formatters/Datum';
import { Input } from '@independent-software/typeui/controls/Input';
import { Flex } from '@independent-software/typeui/controls/Flex';
import { Divider } from '@independent-software/typeui/controls/Divider';
import { Button } from '@independent-software/typeui/controls/Button';

interface IListResultsProps extends IListProps {
  /**
   * If set, list is prefiltered by a sample and
   * sample column and filter are hidden.
   */
  sampleId?: number;  
  /**
   * If set, list is prefiltered by a parameter and
   * parameter column and filter are hidden.
   */
  parameterId?: number;
  /**
   * If set, list is prefiltered by a station and
   * station column and filter are hidden.
   */
  stationId?: number;
  /**
   * Event is fired when a result is clicked.
   */
  onClick?: (result:Result) => void;
}

interface IListResultsState extends IListState<Result> {
  /**
   * Data for campaigns dropdown
   */
  campaigns: Campaign[];
  /**
   * Data for parameters dropdown
   */
  parameters: Parameter[];
  /**
   * Data for stations dropdown
   */
  stations: Station[];
  expandfilter: boolean;
}

class ListResults extends List<Result, IListResultsProps, IListResultsState> {
  constructor(props: IAuthProps & IListResultsProps) {
    super(props, ResultFactory, 'value', 'asc');

    // Initialize state (list initializes its own part of the state):
    this.state = {
      ...this.state,
      campaigns: [],
      parameters: [],
      stations: [],
      expandfilter: false
    };

    if(this.props.sampleId) {
      this.setFilter('sample', 'eq', { id: this.props.sampleId }, true);
    }      
    if(this.props.parameterId) {
      this.setFilter('parameter', 'eq', { id: this.props.parameterId }, true);
    }
    if(this.props.stationId) {
      this.setFilter('station', 'eq',  { id: this.props.stationId }, true);
    }      
  }

  componentDidMount() {
    this.handleCampaignSearch();
    this.handleParameterSearch();
    this.handleStationSearch();
  }  
  
  handleChangeCampaign = (value: Campaign) => {
    this.setFilter('campaign', 'eq', value);
  }

  handleCampaignSearch = (q?:string) => {
    // Retrieve a list of campaigns:
    let query = new Query('date', 'asc');
    if (q) query.setFilter('q', 'like', q);
    CampaignFactory.getSome(this.props.auth, 0, 16, query)
      .then((res) => this.setState({ campaigns: res.items }));
  }    

  handleChangeParameter = (value: Parameter) => {
    this.setFilter('parameter', 'eq', value);
  }

  handleParameterSearch = (q?:string) => {
    // Retrieve a list of parameters:
    let query = new Query('name', 'asc');
    if (q) query.setFilter('q', 'like', q);
    ParameterFactory.getSome(this.props.auth, 0, 16, query)
      .then((res) => this.setState({ parameters: res.items }));
  }  

  handleChangeStation = (value: Station) => {
    this.setFilter('station', 'eq', value);
  }

  handleStationSearch = (q?:string) => {
    // Retrieve a list of stations:
    let query = new Query('name', 'asc');
    if (q) query.setFilter('q', 'like', q);
    StationFactory.getSome(this.props.auth, 0, 16, query)
      .then((res) => this.setState({ stations: res.items }));
  }  

  handleChangeFromDate = (value:string) => {
    this.setFilter('date', 'gte', value);
  }  

  handleChangeToDate = (value:string) => {
    this.setFilter('date', 'lte', value);
  }      

  handleChangeFromValue = (value:string) => {
    this.setFilter('value', 'gte', value);
  }  

  handleChangeToValue = (value:string) => {
    this.setFilter('value', 'lte', value);
  }    

  handleChangeFromDepth = (value:string) => {
    this.setFilter('depth', 'gte', value);
  }  

  handleChangeToDepth = (value:string) => {
    this.setFilter('depth', 'lte', value);
  }    

  render() {
    let p = this.props;

    let filter = 
    <React.Fragment>
      <Panel.Content>
        <Flex.Columns count={2}>
          <>
            {!p.parameterId && 
              <Form.Uncontrolled hint="Filter by parameter">
                <Dropdown onSearch={this.handleParameterSearch} name="parameter" fluid clearable value={this.getFilter('parameter', 'eq')} data={this.state.parameters} placeholder="Parameter" label={(item:Parameter) => item.name} onChange={this.handleChangeParameter}>
                  <Dropdown.Column weight={1}>{(item:Parameter) => item.name}</Dropdown.Column>
                </Dropdown>
              </Form.Uncontrolled>}     
            {!p.stationId && 
              <Form.Uncontrolled hint="Filter by station">
                <Dropdown onSearch={this.handleStationSearch} name="station" fluid clearable value={this.getFilter('station', 'eq')} data={this.state.stations} placeholder="Station" label={(item:Station) => item.name} onChange={this.handleChangeStation}>
                  <Dropdown.Column weight={1}>{(item:Station) => item.name}</Dropdown.Column>
                </Dropdown>
              </Form.Uncontrolled>}     
          </>
          <>
          <Flex.Columns count={2}>
            <Form.Uncontrolled hint="Filter by start date">
              <Input name="from" type="date" fluid clearable value={this.getFilter('date', 'gte')} placeholder="From date" onChange={this.handleChangeFromDate}/>
            </Form.Uncontrolled>
            <Form.Uncontrolled hint="Filter by end date">
              <Input name="to" type="date" fluid clearable value={this.getFilter('date', 'lte')} placeholder="To date" onChange={this.handleChangeToDate}/>
            </Form.Uncontrolled>
          </Flex.Columns>
          <Flex.Columns count={2}>
            <Form.Uncontrolled hint="Minimum value">
              <Input name="fromvalue" type="text" fluid clearable value={this.getFilter('value', 'gte')} placeholder="From value" onChange={this.handleChangeFromValue}/>
            </Form.Uncontrolled>
            <Form.Uncontrolled hint="Maximum value">
              <Input name="tovalue" type="text" fluid clearable value={this.getFilter('value', 'lte')} placeholder="To value" onChange={this.handleChangeToValue}/>
            </Form.Uncontrolled>    
          </Flex.Columns>              
          </>
        </Flex.Columns>
        <Divider hidden><Button size="mini" onClick={() => this.setState({ expandfilter: !this.state.expandfilter })}>{this.state.expandfilter ? 'less' : 'more'}</Button></Divider>
        {this.state.expandfilter && 
        <>
          <Divider hidden/>
          <Flex.Columns count={2}>
            <>
              <Form.Uncontrolled hint="Filter by campaign">
                <Dropdown onSearch={this.handleCampaignSearch} name="sample" fluid clearable value={this.getFilter('campaign', 'eq')} data={this.state.campaigns} placeholder="Campaign" label={(item:Campaign) => item.name} onChange={this.handleChangeCampaign}>
                  <Dropdown.Column weight={1}>{(item:Campaign) => item.name}</Dropdown.Column>
                </Dropdown>
              </Form.Uncontrolled>
            </>
            <Flex.Columns count={2}>  
              <Form.Uncontrolled hint="Minimum depth">
                <Input name="fromdepth" type="text" fluid clearable value={this.getFilter('depth', 'gte')} placeholder="From depth" onChange={this.handleChangeFromDepth}/>
              </Form.Uncontrolled>
              <Form.Uncontrolled hint="Maximum depth">
                <Input name="todepth" type="text" fluid clearable value={this.getFilter('depth', 'lte')} placeholder="To depth" onChange={this.handleChangeToDepth}/>
              </Form.Uncontrolled>    
            </Flex.Columns>                   
          </Flex.Columns>
        </>}
      </Panel.Content>
      <Panel.Footer>
        <div style={{display: "flex", justifyContent: 'space-between'}}>
          <Gate auth={p.auth} right='can_delete_results'>
            <Form.Uncontrolled>
              <Checkbox label="Trash" name="trash" type="toggle" checked={this.getTrash()} onChange={this.handleTrash}/>
            </Form.Uncontrolled>
          </Gate>
          <Export onExport={this.handleExport}/>
        </div>
      </Panel.Footer>      
    </React.Fragment>

    return (
      <React.Fragment>
        <IconBar>
          <Panel.Icon icon="tools" width={650}>
            {filter}
          </Panel.Icon>
        </IconBar> 
        <DataTable error={this.state.error} loading={this.state.loading} scrollTop={this.state.scrollTop} onScroll={this.handleScroll} data={this.state.items} onFetch={this.handleFetch} onClick={p.onClick} onOrder={this.handleOrder} order={this.getOrder()} dir={this.getDir()}>
          {!p.stationId && <DataTable.Column weight={1} label="Station" order="stations.name" dir="asc">{(item:Result) => item.sample.station.name}</DataTable.Column>}
          <DataTable.Column weight={3} label="Campaign">{(item:Result) => item.sample.campaign.name}</DataTable.Column>
          {!p.sampleId && <DataTable.Column weight={2} label="Date" order="samples.date|samples.time" dir="asc">{(item:Result) => item.sample.date ? <><Datum.ShortDate value={item.sample.date}/> <Datum.Time value={item.sample.time}/></> : '-'}</DataTable.Column>}
          {!p.sampleId && <DataTable.Column weight={1} align="right" label="Depth" order="samples.depth" dir="asc">{(item:Result) => item.sample.depth ? <><Number value={item.sample.depth}/> m</> : '-'}</DataTable.Column>}
          {!p.parameterId && <DataTable.Column weight={2} label="Parameter" order="parameters.name" dir="asc">{(item:Result) => item.parameter.name}</DataTable.Column>}
          <DataTable.Column align="right" weight={1} label="Value">{(item:Result) => <Number value={item.value} decimals={item.parameter.decimals}/>}</DataTable.Column>
          <DataTable.Column weight={1} label="Unit">{(item:Result) => item.parameter.unit}</DataTable.Column>
          <DataTable.Column weight={1} label="Detection limit">{(item:Result) => item.detection_limit ? <Icon name="check"/> : <Icon name="times"/>}</DataTable.Column>
        </DataTable>
        <Dialog.Xhr open={this.state.exportError != null} error={this.state.exportError} onClose={this.handleCloseDialog}/>
      </React.Fragment>
    );
  }
}

export { ListResults };
